import { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, CircularProgress } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { cls, useIsDark } from "../../hatim/soft/SoftKit";
import ConnectionState from "../../../components/ui/ConnectionState";

/**
 * Terakki Yönetimi sayfalarının ortak parçaları. Yumuşak tasarım: kalın yazı
 * az, kutu yerine ince çizgili satırlar, açık zemin; koyu temada `dark:`.
 */

/** Grafik renkleri: açıkta derin, koyuda koyu zeminde okunan açık tonlar. */
export function useChartColors() {
  const dark = useIsDark();
  return dark
    ? {
        primary: "#8FC9CA",
        secondary: "#F2C46D",
        tertiary: "#7BE3A8",
        danger: "#F2A59C",
        violet: "#B9B4F2",
        muted: "rgba(255,255,255,0.35)",
        grid: "rgba(255,255,255,0.10)",
        roles: ["#8FC9CA", "#F2C46D", "#B9B4F2", "#F2A59C"],
        dark,
      }
    : {
        primary: "#006A71",
        secondary: "#E8A23A",
        tertiary: "#2E9E6A",
        danger: "#C0392B",
        violet: "#6C63C7",
        muted: "#B7C4C6",
        grid: "#E2E8F0",
        roles: ["#48A6A7", "#E8A23A", "#6C63C7", "#C0392B"],
        dark,
      };
}

export function PageHeader({ title, subtitle, right }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-3xl font-Medium text-terakkiPrimaryDark tracking-tight dark:text-white">{title}</h1>
        {subtitle && <p className={`text-base mt-0.5 ${cls.muted}`}>{subtitle}</p>}
      </div>
      {right && <div className="flex items-center gap-2 flex-wrap">{right}</div>}
    </div>
  );
}

/** Başlıklı, ince çerçeveli bölüm. */
export function Section({ title, subtitle, right, children, className = "", bodyClassName = "p-4" }) {
  return (
    <section className={`${cls.panel} min-w-0 flex flex-col ${className}`}>
      {(title || right) && (
        <header className={`flex items-center gap-3 px-4 py-3 ${cls.row}`}>
          <div className="min-w-0 flex-1">
            {title && <h2 className={`text-md font-Medium ${cls.heading}`}>{title}</h2>}
            {subtitle && <p className={`text-sm ${cls.muted}`}>{subtitle}</p>}
          </div>
          {right}
        </header>
      )}
      <div className={`flex-1 min-w-0 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

export function NoData({ text = "Veri alınamadı" }) {
  return (
    <div className={`py-8 text-center text-base ${cls.muted}`}>{text}</div>
  );
}

/** Yönetim ekranlarının hata durumu: ortak ConnectionState (bağlantı, bakım, hata). */
export function ErrorRetry({ message, onRetry, error, retrying }) {
  return <ConnectionState error={error} onRetry={onRetry} retrying={retrying} message={message} />;
}

export function SkeletonLine({ className = "h-4 w-full" }) {
  return <div className={`rounded ${cls.skeleton} ${className}`} />;
}

export function SkeletonRows({ count = 5 }) {
  return (
    <ul>
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className={`px-4 py-3.5 flex flex-col gap-2 ${cls.row}`}>
          <SkeletonLine className="h-4 w-1/2" />
          <SkeletonLine className="h-3 w-1/3" />
        </li>
      ))}
    </ul>
  );
}

/** Etiket · değer satırı (kutusuz, alt çizgili). */
export function StatRow({ label, value, hint, dot }) {
  return (
    <li className={`flex items-center gap-3 px-4 py-2.5 ${cls.row} last:border-b-0`}>
      {dot && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dot }} aria-hidden />}
      <span className={`flex-1 min-w-0 text-base ${cls.muted}`}>
        {label}
        {hint && <span className={`block text-xs ${cls.faint}`}>{hint}</span>}
      </span>
      <span className={`text-md tabular-nums ${cls.title}`}>{value}</span>
    </li>
  );
}

/** Sayfalama: önceki · sayfa · sonraki. */
export function Pager({ page, hasMore, onChange, busy }) {
  if (page <= 1 && !hasMore) return null;
  const btn =
    "inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-terakkiPrimaryDark hover:bg-terakkiBrandCloud disabled:opacity-40 disabled:hover:bg-transparent dark:text-[#8FC9CA] dark:hover:bg-white/[0.08]";
  return (
    <div className={`flex items-center justify-between gap-2 px-3 py-2 ${cls.footer}`}>
      <button type="button" className={btn} disabled={page <= 1 || busy} onClick={() => onChange(page - 1)}>
        <ChevronLeftIcon sx={{ fontSize: 18 }} aria-hidden /> Önceki
      </button>
      <span className={`text-sm ${cls.muted}`}>Sayfa {page}</span>
      <button type="button" className={btn} disabled={!hasMore || busy} onClick={() => onChange(page + 1)}>
        Sonraki <ChevronRightIcon sx={{ fontSize: 18 }} aria-hidden />
      </button>
    </div>
  );
}

/** Onay penceresi. */
export function ConfirmDialog({ open, title, text, confirmLabel = "Onayla", danger, busy, onConfirm, onClose }) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 500 }}>{title}</DialogTitle>
      {text && <DialogContent sx={{ fontSize: 14 }}>{text}</DialogContent>}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Vazgeç
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          variant="contained"
          color={danger ? "error" : "primary"}
          disableElevation
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Küçük yumuşak durum etiketi. tone: neutral | ok | warn | danger | brand */
export function Tag({ tone = "neutral", children }) {
  const tones = {
    neutral: "bg-terakkiSurfaceAlt text-terakkiNeutral dark:bg-white/[0.08] dark:text-white/70",
    ok: "bg-terakkiSuccess/10 text-terakkiSuccess dark:bg-[#7BE3A8]/15 dark:text-[#7BE3A8]",
    warn: "bg-terakkiWarning/15 text-[#9A6412] dark:bg-[#F2C46D]/15 dark:text-[#F2C46D]",
    danger: "bg-terakkiError/10 text-terakkiError dark:bg-[#F2A59C]/15 dark:text-[#F2A59C]",
    brand: "bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-white/10 dark:text-[#8FC9CA]",
  };
  return (
    <span className={`inline-flex items-center shrink-0 text-[11px] px-2 py-0.5 rounded-full ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

/** Arama kutusu (Tailwind, koyu tema uyumlu). */
export function SearchInput({ value, onChange, placeholder, className = "" }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={100}
      className={`h-10 px-3 rounded-lg border border-terakkiBorder bg-terakkiSurface text-base text-terakkiInk outline-none focus:border-terakkiPrimary ${cls.input} ${className}`}
    />
  );
}

/** Basit seçim kutusu. options: [{ value, label }] */
export function SelectInput({ value, onChange, options, label, className = "" }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className={`h-10 px-2.5 rounded-lg border border-terakkiBorder bg-terakkiSurface text-base text-terakkiInk outline-none focus:border-terakkiPrimary dark:bg-[#17474A] dark:text-white dark:border-white/[0.14] ${className}`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Yazarken her tuşta istek atmamak için gecikmeli değer. */
export function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Sekme şeridi (alt çizgili). tabs: [{ value, label, count }] */
export function TabStrip({ tabs, value, onChange }) {
  return (
    <div role="tablist" className={`flex gap-1 overflow-x-auto ${cls.row}`}>
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={[
              "shrink-0 px-3 py-2.5 text-base whitespace-nowrap border-b-2 -mb-px transition-colors",
              active
                ? "border-terakkiPrimaryDark text-terakkiPrimaryDark dark:border-[#8FC9CA] dark:text-white"
                : "border-transparent text-terakkiNeutral hover:text-terakkiPrimaryDark dark:text-white/60 dark:hover:text-white",
            ].join(" ")}
          >
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <span className={`ml-1.5 text-sm ${active ? "" : "opacity-70"}`}>· {tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
